// File: frontend/src/utils/inlineMarkup.tsx

import React from "react";
import { Link } from "react-router-dom";
import { EntryImage, collectEntryImageUrls, normalizeImageTokenSpacing } from "../components/entries/EntryImage";

// ─── Inner markup for block token contents ────────────────────────────────────
// Tokens allowed inside (alinti: ...) and (spoiler: ...) blocks, written via
// the block modal's full toolbar. Mirrors the patterns in EntryRow.tsx,
// excluding the block tokens themselves (alinti/spoiler cannot nest).
const INNER_BOLD = /\(b:\s*(.*?)\)/g;
const INNER_ITALIC = /\(i:\s*(.*?)\)/g;
const INNER_LINK = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
// <ref> is an opaque image id (new format) or a full URL (legacy entries).
const INNER_GORSEL = /\(gorsel:\s*(https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;
// Inline entity refs — label is the link
const INNER_REFS: Array<{ pattern: RegExp; keyPrefix: string; buildTo: (tail: string) => string; buildTitle: (label: string) => string; className: string }> = [
  { pattern: /\(mac:\s*(.*?)\|\|(\d+)\)/g, keyPrefix: "mac", buildTo: (tail) => `/mac--${tail}`, buildTitle: (label) => `maça git: ${label}`, className: "entry-ref-link" },
  { pattern: /\(kisi:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "kisi", buildTo: (tail) => `/kisi/${tail}`, buildTitle: (label) => `kişiye git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(kulup:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "kulup", buildTo: (tail) => `/kulup/${tail}`, buildTitle: (label) => `kulübe git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(takim:\s*(.*?)\|\|([^)\s]+)\)/g, keyPrefix: "takim", buildTo: (tail) => `/takim/${tail}`, buildTitle: (label) => `takıma git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(baslik:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "baslik", buildTo: (tail) => `/gundem/${tail}`, buildTitle: (label) => `başlığa git: ${label}`, className: "entry-person-ref-link" },
];
// "(bkz: ...)" variants — rendered as a parenthesized note
const INNER_BKZ_REFS: Array<{ pattern: RegExp; keyPrefix: string; buildTo: (tail: string) => string; buildTitle: (label: string) => string; className: string }> = [
  { pattern: /\(bkz-mac:\s*(.*?)\|\|(\d+)\)/g, keyPrefix: "bkz-mac", buildTo: (tail) => `/mac--${tail}`, buildTitle: (label) => `maça git: ${label}`, className: "entry-ref-link" },
  { pattern: /\(bkz-kisi:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kisi", buildTo: (tail) => `/kisi/${tail}`, buildTitle: (label) => `kişiye git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(bkz-kulup:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kulup", buildTo: (tail) => `/kulup/${tail}`, buildTitle: (label) => `kulübe git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(bkz-takim:\s*(.*?)\|\|([^)\s]+)\)/g, keyPrefix: "bkz-takim", buildTo: (tail) => `/takim/${tail}`, buildTitle: (label) => `takıma git: ${label}`, className: "entry-person-ref-link" },
  { pattern: /\(bkz-baslik:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-baslik", buildTo: (tail) => `/gundem/${tail}`, buildTitle: (label) => `başlığa git: ${label}`, className: "entry-person-ref-link" },
];

interface InnerSpan {
  index: number;
  length: number;
  node: React.ReactNode;
}

/**
 * Render the inline markup allowed inside block tokens — bold, italic,
 * external links, inline images, entity refs, and their "(bkz: ...)"
 * variants. Used for the content of (alinti: ...) quotes and
 * (spoiler: ...) blocks in EntryRow, previews, and SpoilerBlock.
 */
// The optional `gallery` argument carries every image URL of the parent entry,
// so an image nested inside an alıntı/spoiler block still opens a lightbox that
// can navigate to the entry's other images. When omitted, only the images found
// in `text` itself form the gallery.
export function renderBasicMarkup(text: string, gallery?: string[]): React.ReactNode[] {
  // Same image-token line-break cleanup as EntryRow's renderContent.
  text = normalizeImageTokenSpacing(text);
  const galleryUrls = gallery && gallery.length > 0 ? gallery : collectEntryImageUrls(text);
  const spans: InnerSpan[] = [];
  let m: RegExpExecArray | null;

  INNER_BOLD.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = INNER_BOLD.exec(text)) !== null) {
    spans.push({ index: m.index, length: m[0].length, node: <strong key={`ib-${m.index}`}>{m[1]}</strong> });
  }

  INNER_ITALIC.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = INNER_ITALIC.exec(text)) !== null) {
    spans.push({ index: m.index, length: m[0].length, node: <em key={`ii-${m.index}`}>{m[1]}</em> });
  }

  // External link — routed through the external link warning page, like EntryRow
  INNER_LINK.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = INNER_LINK.exec(text)) !== null) {
    const url = m[1].trim();
    const displayText = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link key={`il-${m.index}`} to={`/dis-link?url=${encodeURIComponent(url)}`} className="entry-ext-link" title={url}>
          {displayText}
        </Link>
      ),
    });
  }

  // Inline image block
  INNER_GORSEL.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = INNER_GORSEL.exec(text)) !== null) {
    const url = m[1].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        // Rendered as a <span> wrapper: this markup can sit inside an inline
        // context, where a <div> would be invalid HTML.
        <EntryImage key={`ig-${m.index}`} src={url} gallery={galleryUrls} as="span" />
      ),
    });
  }

  // Inline entity refs — the label itself is the link
  for (const variant of INNER_REFS) {
    variant.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    while ((m = variant.pattern.exec(text)) !== null) {
      const label = m[1].trim();
      const tail = m[2].trim();
      spans.push({
        index: m.index,
        length: m[0].length,
        node: (
          <Link key={`${variant.keyPrefix}-${m.index}`} to={variant.buildTo(tail)} className={variant.className} title={variant.buildTitle(label)}>
            {label}
          </Link>
        ),
      });
    }
  }

  // "(bkz: ...)" variants — parenthesized note, only the label is a link
  for (const variant of INNER_BKZ_REFS) {
    variant.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    while ((m = variant.pattern.exec(text)) !== null) {
      const label = m[1].trim();
      const tail = m[2].trim();
      spans.push({
        index: m.index,
        length: m[0].length,
        node: (
          <span key={`${variant.keyPrefix}-${m.index}`} className="entry-bkz-ref">
            {"(bkz: "}
            <Link to={variant.buildTo(tail)} className={variant.className} title={variant.buildTitle(label)}>
              {label}
            </Link>
            {")"}
          </span>
        ),
      });
    }
  }

  spans.sort((a, b) => a.index - b.index);

  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const s of spans) {
    // Skip overlapping spans — defensive against malformed nesting
    if (s.index < last) continue;
    if (s.index > last) nodes.push(text.slice(last, s.index));
    nodes.push(s.node);
    last = s.index + s.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}